import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { PermissionLevel } from '../constants/permissionLevels';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { withAlpha } from '../theme';

interface PermissionLevelPickerProps {
  levels: PermissionLevel[];
  value: PermissionLevel;
  onChange: (level: PermissionLevel) => void;
  /** True when this value is resolved from a department/role default rather than an
   * explicit override on this target — rendered with a dashed/outlined style so it's
   * visually distinct from an explicit selection. */
  inherited?: boolean;
}

const LEVEL_KEY: Record<PermissionLevel, string> = {
  none: 'permissions.levels.none',
  view: 'permissions.levels.view',
  edit: 'permissions.levels.edit',
  admin: 'permissions.levels.admin',
};

export const PermissionLevelPicker: React.FC<PermissionLevelPickerProps> = ({ levels, value, onChange, inherited }) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();

  return (
    <View style={[styles.container, { flexDirection: rtl.rowDirection }, inherited && styles.containerInherited]}>
      {levels.map((level) => {
        const isSelected = level === value;
        return (
          <TouchableOpacity
            key={level}
            style={[
              styles.segment,
              isSelected && styles.segmentSelected,
              isSelected && inherited && styles.segmentSelectedInherited,
            ]}
            onPress={() => onChange(level)}
            activeOpacity={0.7}
          >
            <Text style={[styles.segmentText, isSelected && styles.segmentTextSelected]}>
              {t(LEVEL_KEY[level] as any)}
            </Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.background.secondary,
    padding: 2,
  },
  containerInherited: {
    borderWidth: 1,
    borderColor: colors.border,
    borderStyle: 'dashed',
  },
  segment: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: designTokens.borderRadius.sm,
  },
  segmentSelected: {
    backgroundColor: colors.primary,
  },
  segmentSelectedInherited: {
    backgroundColor: withAlpha(colors.primary, 0.38),
  },
  segmentText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.secondary,
  },
  segmentTextSelected: {
    color: colors.white,
    fontFamily: typography.fontFamily.semibold,
  },
});
